import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import { Save, Upload, X, Sparkles, Type } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Playfair+Display:wght@700&family=Montserrat:wght@700&family=Raleway:wght@700&family=DM+Sans:wght@700&family=Oswald:wght@700&family=Cormorant+Garamond:wght@700&family=Space+Grotesk:wght@700&family=Lora:wght@700&family=Anton&display=swap";

const FONT_STYLES = [
  { id: "bebas-neue",         label: "Bebas Neue",         family: "'Bebas Neue', cursive",         desc: "Bold & impactful",  isDefault: false },
  { id: "montserrat",         label: "Montserrat",         family: "'Montserrat', sans-serif",       desc: "Clean modern",     isDefault: true  },
  { id: "playfair-display",   label: "Playfair Display",   family: "'Playfair Display', serif",      desc: "Classic & elegant", isDefault: false },
  { id: "raleway",            label: "Raleway",            family: "'Raleway', sans-serif",          desc: "Stylish & refined", isDefault: false },
  { id: "dm-sans",            label: "DM Sans",            family: "'DM Sans', sans-serif",          desc: "Minimal & clean",  isDefault: false },
  { id: "oswald",             label: "Oswald",             family: "'Oswald', sans-serif",           desc: "Strong & condensed", isDefault: false },
  { id: "cormorant-garamond", label: "Cormorant Garamond", family: "'Cormorant Garamond', serif",    desc: "Luxury serif",     isDefault: false },
  { id: "space-grotesk",      label: "Space Grotesk",      family: "'Space Grotesk', sans-serif",    desc: "Tech & modern",    isDefault: false },
  { id: "lora",               label: "Lora",               family: "'Lora', serif",                  desc: "Editorial serif",  isDefault: false },
  { id: "anton",              label: "Anton",              family: "'Anton', sans-serif",            desc: "Heavy display",    isDefault: false },
];

// 3×3 positions — order matches grid rows: top, center, bottom
const LOGO_POSITIONS = [
  { id: "top-left",      row: 0, col: 0 },
  { id: "top-center",    row: 0, col: 1 },
  { id: "top-right",     row: 0, col: 2 },
  { id: "center-left",   row: 1, col: 0 },
  { id: "center",        row: 1, col: 1 },
  { id: "center-right",  row: 1, col: 2 },
  { id: "bottom-left",   row: 2, col: 0 },
  { id: "bottom-center", row: 2, col: 1 },
  { id: "bottom-right",  row: 2, col: 2 },
];

const TONES = ["Bold", "Luxury", "Funny", "Minimal", "Tech", "Local"];

// Returns CSS positioning for the logo overlay inside the phone screen
function getLogoCSS(position, scalePct) {
  const M = "6px";
  const translate = {
    "top-left":      { top: M, left: M },
    "top-center":    { top: M, left: "50%", transform: "translateX(-50%)" },
    "top-right":     { top: M, right: M },
    "center-left":   { top: "50%", left: M, transform: "translateY(-50%)" },
    "center":        { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
    "center-right":  { top: "50%", right: M, transform: "translateY(-50%)" },
    "bottom-left":   { bottom: M, left: M },
    "bottom-center": { bottom: M, left: "50%", transform: "translateX(-50%)" },
    "bottom-right":  { bottom: M, right: M },
  };
  return {
    position: "absolute",
    width: `${scalePct}%`,
    maxWidth: `${scalePct}%`,
    zIndex: 20,
    pointerEvents: "none",
    ...(translate[position] || translate["bottom-right"]),
  };
}

// Phone mockup component — 9:16 screen, clickable position zones, live logo preview
function PhoneMockup({ logoBase64, logoPosition, logoScale, onPositionChange }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(null);
  const scalePct = Math.round(logoScale * 100);

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Phone shell */}
      <div
        style={{
          width: 168,
          height: 340,
          background: "#111",
          borderRadius: 36,
          border: "2.5px solid #2d2d2d",
          boxShadow: "0 0 0 1px #1a1a1a, 0 20px 40px rgba(0,0,0,0.6)",
          padding: "10px 5px 14px",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {/* Dynamic Island */}
        <div style={{
          position: "absolute", top: 7, left: "50%", transform: "translateX(-50%)",
          width: 44, height: 9, background: "#0a0a0a", borderRadius: 8, zIndex: 30,
        }} />

        {/* Side buttons */}
        <div style={{ position: "absolute", left: -3, top: 70, width: 3, height: 28, background: "#222", borderRadius: "3px 0 0 3px" }} />
        <div style={{ position: "absolute", left: -3, top: 106, width: 3, height: 44, background: "#222", borderRadius: "3px 0 0 3px" }} />
        <div style={{ position: "absolute", left: -3, top: 158, width: 3, height: 44, background: "#222", borderRadius: "3px 0 0 3px" }} />
        <div style={{ position: "absolute", right: -3, top: 100, width: 3, height: 60, background: "#222", borderRadius: "0 3px 3px 0" }} />

        {/* Screen */}
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: 30,
            overflow: "hidden",
            position: "relative",
            background: "linear-gradient(170deg, #1a0e2e 0%, #0d0d14 35%, #0a0e1a 65%, #111827 100%)",
          }}
        >
          {/* Simulated cinematic ad frame */}
          <div style={{ position: "absolute", inset: 0, opacity: 0.4 }}>
            <div style={{
              position: "absolute", top: "20%", left: "10%", right: "10%", bottom: "20%",
              background: "radial-gradient(ellipse at 40% 50%, rgba(124,58,237,0.2) 0%, transparent 70%)",
            }} />
            <div style={{
              position: "absolute", bottom: "15%", left: "5%", right: "5%",
              height: "1px", background: "rgba(255,255,255,0.06)",
            }} />
            <div style={{
              position: "absolute", top: "12%", left: "8%",
              width: "55%", height: "6px", background: "rgba(255,255,255,0.07)", borderRadius: 3,
            }} />
            <div style={{
              position: "absolute", top: "22%", left: "8%",
              width: "35%", height: "4px", background: "rgba(255,255,255,0.04)", borderRadius: 3,
            }} />
            <div style={{
              position: "absolute", bottom: "22%", left: "8%",
              width: "45%", height: "5px", background: "rgba(255,255,255,0.06)", borderRadius: 3,
            }} />
            <div style={{
              position: "absolute", bottom: "14%", left: "8%",
              width: "28%", height: "4px", background: "rgba(255,255,255,0.04)", borderRadius: 3,
            }} />
          </div>

          {/* Clickable 3×3 zone grid */}
          <div style={{
            position: "absolute", inset: 0,
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gridTemplateRows: "1fr 1fr 1fr",
            zIndex: 10,
          }}>
            {LOGO_POSITIONS.map(pos => (
              <button
                key={pos.id}
                type="button"
                onClick={() => onPositionChange(pos.id)}
                onMouseEnter={() => setHovered(pos.id)}
                onMouseLeave={() => setHovered(null)}
                data-testid={`phone-zone-${pos.id}`}
                style={{
                  background: logoPosition === pos.id
                    ? "rgba(124,58,237,0.18)"
                    : hovered === pos.id
                    ? "rgba(255,255,255,0.06)"
                    : "transparent",
                  border: logoPosition === pos.id ? "1px solid rgba(124,58,237,0.4)" : "1px solid transparent",
                  cursor: "crosshair",
                  transition: "background 0.15s",
                }}
              />
            ))}
          </div>

          {/* Logo overlay */}
          {logoBase64 ? (
            <img
              src={`data:image/png;base64,${logoBase64}`}
              alt="logo preview"
              style={getLogoCSS(logoPosition, scalePct)}
            />
          ) : (
            <div style={{ ...getLogoCSS(logoPosition, scalePct), pointerEvents: "none" }}>
              <div style={{
                background: "rgba(255,255,255,0.12)",
                border: "1px dashed rgba(255,255,255,0.25)",
                borderRadius: 4,
                padding: "2px 5px",
                fontSize: 8,
                color: "rgba(255,255,255,0.5)",
                whiteSpace: "nowrap",
                fontWeight: 700,
                letterSpacing: 1,
              }}>
                LOGO
              </div>
            </div>
          )}
        </div>

        {/* Home indicator */}
        <div style={{
          position: "absolute", bottom: 5, left: "50%", transform: "translateX(-50%)",
          width: 40, height: 3, background: "#2d2d2d", borderRadius: 3,
        }} />
      </div>

      <p className="text-xs text-center" style={{ color: "#6b6b6b" }}>
        {t("brandKit.clickToPosition")}
      </p>
    </div>
  );
}

export default function BrandKit() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_name: "",
    font_style: "montserrat",
    tone: "Bold",
    logo_base64: null,
    logo_position: "bottom-right",
    logo_scale: 0.14,
  });

  useEffect(() => {
    if (!document.getElementById("brand-kit-fonts")) {
      const link = document.createElement("link");
      link.id = "brand-kit-fonts";
      link.rel = "stylesheet";
      link.href = GOOGLE_FONTS_URL;
      document.head.appendChild(link);
    }
    fetchKit();
  }, []);

  const fetchKit = async () => {
    try {
      const res = await axios.get(`${API}/brand-kit`, { withCredentials: true });
      const d = res.data;
      const validFont = FONT_STYLES.find(f => f.id === d.font_style) ? d.font_style : "montserrat";
      setForm({
        business_name: d.business_name || "",
        font_style: validFont,
        tone: d.tone || "Bold",
        logo_base64: d.logo_base64 || null,
        logo_position: d.logo_position || "bottom-right",
        logo_scale: d.logo_scale ?? 0.14,
      });
    } catch { toast.error(t("brandKit.toasts.loadFailed")); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: form.business_name,
        font_style: form.font_style,
        tone: form.tone,
        logo_position: form.logo_position,
        logo_scale: form.logo_scale,
        logo_base64: form.logo_base64 ?? null,
      };
      await axios.put(`${API}/brand-kit`, payload, { withCredentials: true });
      toast.success(t("brandKit.toasts.saved"));
    } catch { toast.error(t("brandKit.toasts.saveFailed")); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t("brandKit.toasts.logoTooLarge")); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, logo_base64: ev.target.result.split(",")[1] }));
    reader.readAsDataURL(file);
  };

  const scalePercent = Math.round(form.logo_scale * 100);

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center py-16">
        <div className="animate-pulse text-zinc-400">{t("brandKit.loading")}</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-white" style={{ fontFamily: "Epilogue, sans-serif" }}>{t("brandKit.title")}</h1>
            <p className="text-zinc-400 text-sm mt-1">{t("brandKit.subtitle")}</p>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-brand-kit-btn"
            className="bg-purple-600 hover:bg-purple-500 text-white rounded-full px-6">
            <Save className="w-4 h-4 mr-2" />
            {saving ? t("common.saving") : t("brandKit.saveKit")}
          </Button>
        </div>

        <div className="space-y-6">

          {/* Brand Identity */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-zinc-700 p-6" style={{ background: "#161616" }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(168,85,247,0.1)" }}>
                <Sparkles className="w-4 h-4 text-purple-400" />
              </div>
              <h2 className="font-bold text-white" style={{ fontFamily: "Epilogue, sans-serif" }}>{t("brandKit.identity")}</h2>
            </div>

            <div className="space-y-6">
              {/* Business Name */}
              <div>
                <Label className="text-sm font-medium text-zinc-300">{t("brandKit.businessName")}</Label>
                <Input placeholder={t("brandKit.businessNamePlaceholder")} value={form.business_name}
                  onChange={e => setForm({ ...form, business_name: e.target.value })}
                  data-testid="kit-business-name"
                  className="mt-1 h-11 rounded-xl text-white" style={{ background: "#1c1c1c", border: "1px solid #333" }} />
              </div>

              {/* Logo upload */}
              <div>
                <Label className="text-sm font-medium text-zinc-300">{t("brandKit.logo")}</Label>
                <div className="mt-2 flex items-center gap-4">
                  {form.logo_base64 ? (
                    <div className="relative">
                      <img src={`data:image/png;base64,${form.logo_base64}`} alt="Logo"
                        className="w-16 h-16 rounded-xl object-contain border border-zinc-700 p-1"
                        style={{ background: "#1c1c1c" }} />
                      <button onClick={() => setForm(f => ({ ...f, logo_base64: null }))}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 border-2 border-dashed border-zinc-700 rounded-xl flex items-center justify-center"
                      style={{ background: "#1c1c1c" }}>
                      <Upload className="w-6 h-6 text-zinc-400" />
                    </div>
                  )}
                  <label className="cursor-pointer" data-testid="logo-upload-label">
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload-input" />
                    <span className="text-sm font-medium text-zinc-300 hover:text-white border border-zinc-700 px-4 py-2 rounded-lg transition-colors"
                      style={{ background: "#1c1c1c" }}>
                      {form.logo_base64 ? t("brandKit.changeLogo") : t("brandKit.uploadLogo")}
                    </span>
                  </label>
                  <span className="text-xs text-zinc-500">{t("brandKit.logoHint")}</span>
                </div>
              </div>

              {/* Logo Position & Scale — side by side */}
              <div className="flex gap-6 items-start">
                {/* Phone mockup */}
                <PhoneMockup
                  logoBase64={form.logo_base64}
                  logoPosition={form.logo_position}
                  logoScale={form.logo_scale}
                  onPositionChange={pos => setForm(f => ({ ...f, logo_position: pos }))}
                />

                {/* Scale slider + position label */}
                <div className="flex-1 pt-2 space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium text-zinc-300">{t("brandKit.logoSize")}</Label>
                      <span className="text-sm font-bold text-purple-400">{scalePercent}%</span>
                    </div>
                    <input type="range" min={5} max={30} step={1}
                      value={scalePercent}
                      onChange={e => setForm(f => ({ ...f, logo_scale: parseInt(e.target.value) / 100 }))}
                      data-testid="logo-scale-slider"
                      className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                      style={{ accentColor: "#7c3aed", background: "#2d2d2d" }} />
                    <div className="flex justify-between text-xs mt-1" style={{ color: "#6b6b6b" }}>
                      <span>{t("brandKit.small")}</span><span>{t("brandKit.large")}</span>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-zinc-300 mb-1 block">{t("brandKit.position")}</Label>
                    <p className="text-sm capitalize" style={{ color: "#c084fc" }}>
                      {t(`brandKit.positions.${form.logo_position}`)}
                    </p>
                    <p className="text-xs mt-1" style={{ color: "#6b6b6b" }}>
                      {t("brandKit.positionHint")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Style Preferences */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl border border-zinc-700 p-6" style={{ background: "#161616" }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(96,165,250,0.1)" }}>
                <Type className="w-4 h-4 text-blue-400" />
              </div>
              <h2 className="font-bold text-white" style={{ fontFamily: "Epilogue, sans-serif" }}>{t("brandKit.stylePrefs")}</h2>
            </div>

            {/* Default Tone */}
            <div className="mb-6">
              <Label className="text-sm font-medium text-zinc-300 mb-2 block">{t("brandKit.defaultTone")}</Label>
              <div className="flex flex-wrap gap-2">
                {TONES.map(tone => (
                  <button key={tone} type="button"
                    onClick={() => setForm(f => ({ ...f, tone }))}
                    data-testid={`tone-${tone.toLowerCase()}`}
                    className="px-4 py-1.5 rounded-full text-sm font-medium transition-all"
                    style={{
                      background: form.tone === tone ? "rgba(124,58,237,0.25)" : "#1c1c1c",
                      border: `1px solid ${form.tone === tone ? "#7c3aed" : "#2d2d2d"}`,
                      color: form.tone === tone ? "#c084fc" : "#6b6b6b",
                    }}>
                    {t(`tones.${tone}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Font picker */}
            <div>
              <Label className="text-sm font-medium text-zinc-300 mb-3 block">{t("brandKit.fontStyle")}</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="font-grid">
                {FONT_STYLES.map(f => {
                  const selected = form.font_style === f.id;
                  return (
                    <button key={f.id} type="button"
                      onClick={() => setForm(prev => ({ ...prev, font_style: f.id }))}
                      data-testid={`font-${f.id}`}
                      className="flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all"
                      style={{
                        background: selected ? "rgba(124,58,237,0.15)" : "#1c1c1c",
                        border: `1px solid ${selected ? "#7c3aed" : "#2d2d2d"}`,
                      }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span style={{ fontFamily: f.family, fontSize: "1.05rem", color: selected ? "#ede9fe" : "#a0a0a0", whiteSpace: "nowrap" }}>
                          {f.label}
                        </span>
                        {f.isDefault && (
                          <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                            style={{ background: "rgba(124,58,237,0.2)", color: "#a78bfa", fontSize: "0.6rem", fontFamily: "inherit" }}>
                            {t("brandKit.default")}
                          </span>
                        )}
                      </div>
                      <span className="text-xs ml-2 flex-shrink-0" style={{ color: "#6b6b6b", fontFamily: "inherit" }}>{t(`brandKit.fonts.${f.id}`)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Save button */}
          <Button onClick={handleSave} disabled={saving} data-testid="save-brand-kit-bottom-btn"
            className="w-full h-12 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl text-base">
            <Save className="w-5 h-5 mr-2" />
            {saving ? t("common.saving") : t("brandKit.saveBrandKit")}
          </Button>

        </div>
      </div>
    </Layout>
  );
}
